import React, { useState, useEffect } from 'react';
import { processPaperWithGemini } from '../services/geminiService';
import { PaperData } from '../types/paper';
import { Upload, Link as LinkIcon, FileText, Loader2, Sparkles, AlertCircle, Compass, CheckCircle2 } from 'lucide-react';

interface PaperIngestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaperProcessed: (paper: PaperData) => void;
}

export const PaperIngestModal: React.FC<PaperIngestModalProps> = ({
  isOpen,
  onClose,
  onPaperProcessed
}) => {
  const [tab, setTab] = useState<'topic' | 'arxiv' | 'pdf'>('topic');
  const [topicInput, setTopicInput] = useState<string>('CRISPR-Cas9 Gene Editing');
  const [arxivInput, setArxivInput] = useState<string>('https://arxiv.org/abs/1706.03762');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<number>(1);

  useEffect(() => {
    let interval: any;
    if (isProcessing) {
      setActiveStep(1);
      interval = setInterval(() => {
        setActiveStep(prev => (prev < 4 ? prev + 1 : prev));
      }, 1500);
    } else {
      setActiveStep(1);
    }
    return () => clearInterval(interval);
  }, [isProcessing]);

  if (!isOpen) return null;

  const handleProcessTopic = async () => {
    if (!topicInput.trim()) return;
    setIsProcessing(true);
    setError(null);

    try {
      const promptText = `Topic/Domain Query: ${topicInput}\nPlease derive complete concept wiki, historical lineage & reference ancestry, and high-yield flashcards with mathematical rigor.`;
      
      const paperData = await processPaperWithGemini(promptText);
      onPaperProcessed(paperData);
      setIsProcessing(false);
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to process topic with Gemini API. Check your API key.');
      setIsProcessing(false);
    }
  };

  const handleProcessArxiv = async () => {
    if (!arxivInput.trim()) return;
    setIsProcessing(true);
    setError(null);

    try {
      const match = arxivInput.match(/(\d{4}\.\d{4,5})/);
      const arxivId = match ? match[1] : '1706.03762';

      const promptText = `arXiv ID: ${arxivId}\nLink: ${arxivInput}\nPlease derive complete concept wiki, reference lineage, and high-yield flashcards.`;
      
      const paperData = await processPaperWithGemini(promptText);
      paperData.arxivId = arxivId;

      onPaperProcessed(paperData);
      setIsProcessing(false);
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to process paper with Gemini API. Check your API key.');
      setIsProcessing(false);
    }
  };

  const handleProcessPdf = async () => {
    if (!pdfFile) return;
    setIsProcessing(true);
    setError(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = (e.target?.result as string).split(',')[1];

        const paperData = await processPaperWithGemini(`Filename: ${pdfFile.name}`, base64);
        onPaperProcessed(paperData);
        setIsProcessing(false);
        onClose();
      };
      reader.onerror = () => {
        setError('Error reading PDF file.');
        setIsProcessing(false);
      };
      reader.readAsDataURL(pdfFile);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to process PDF.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-4 flex items-center justify-center animate-fade-in">
      <div className="relative max-w-xl w-full bg-dark-card border border-slate-700/80 p-6 rounded-2xl shadow-2xl space-y-5">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <div className="flex items-center gap-2 text-brand-glow font-bold text-lg">
            <Sparkles className="w-5 h-5 text-brand-400" /> Derive Knowledge Wiki & Flashcards
          </div>
          <button onClick={onClose} disabled={isProcessing} className="text-slate-400 hover:text-white p-1 rounded-lg">✕</button>
        </div>

        {/* Tab Toggle */}
        <div className="flex p-1 bg-dark-bg/90 rounded-xl border border-slate-800">
          <button
            onClick={() => setTab('topic')}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
              tab === 'topic'
                ? 'bg-brand-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Compass className="w-3.5 h-3.5" /> Any Topic
          </button>
          <button
            onClick={() => setTab('arxiv')}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
              tab === 'arxiv'
                ? 'bg-brand-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <LinkIcon className="w-3.5 h-3.5" /> arXiv Paper
          </button>
          <button
            onClick={() => setTab('pdf')}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
              tab === 'pdf'
                ? 'bg-brand-600 text-white shadow-glow-blue'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Upload className="w-3.5 h-3.5" /> Upload PDF
          </button>
        </div>

        {/* Topic Input Tab */}
        {tab === 'topic' && (
          <div className="space-y-3">
            <label className="text-xs font-semibold text-slate-300">Enter Any Research Topic or Domain</label>
            <input
              type="text"
              value={topicInput}
              onChange={e => setTopicInput(e.target.value)}
              placeholder="e.g. Quantum Computing, Raft Distributed Consensus, RLHF, Neural Radiance Fields"
              className="w-full bg-dark-bg border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-white font-sans focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
            <div className="flex flex-wrap gap-1.5 text-[11px] text-slate-400">
              <span>Quick Suggestions:</span>
              {(['CRISPR-Cas9 Gene Editing', 'Quantum Key Distribution', 'RLHF & PPO Alignment', 'Raft Consensus'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setTopicInput(s)}
                  className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-slate-700"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* arXiv Tab Input */}
        {tab === 'arxiv' && (
          <div className="space-y-3">
            <label className="text-xs font-semibold text-slate-300">arXiv URL or Abstract ID</label>
            <input
              type="text"
              value={arxivInput}
              onChange={e => setArxivInput(e.target.value)}
              placeholder="e.g. https://arxiv.org/abs/1706.03762 or 1706.03762"
              className="w-full bg-dark-bg border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>
        )}

        {/* PDF Tab Input */}
        {tab === 'pdf' && (
          <div className="space-y-3">
            <label className="text-xs font-semibold text-slate-300">Upload PDF File</label>
            <div className="border-2 border-dashed border-slate-700 hover:border-brand-500 rounded-2xl p-6 text-center bg-dark-bg/60 transition-all cursor-pointer">
              <input
                type="file"
                accept="application/pdf"
                onChange={e => setPdfFile(e.target.files?.[0] || null)}
                className="hidden"
                id="pdf-upload-input"
              />
              <label htmlFor="pdf-upload-input" className="cursor-pointer space-y-2 block">
                <FileText className="w-8 h-8 text-brand-400 mx-auto" />
                <div className="text-sm font-semibold text-slate-200">
                  {pdfFile ? pdfFile.name : 'Click to select or drop PDF file'}
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {error && (
          <div className="p-3.5 bg-rose-950/90 border border-rose-500/50 rounded-xl text-rose-200 text-xs flex flex-col gap-2">
            <div className="flex items-center gap-2 font-bold text-rose-300">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
              API Server Overloaded
            </div>
            <div className="text-rose-200 leading-relaxed">
              Google Gemini's public server experienced a brief overload. Click <strong>"Retry Generation"</strong> or select one of the instant demo buttons on the top bar!
            </div>
          </div>
        )}

        {/* Animated Extraction Stepper */}
        {isProcessing && (
          <div className="p-4 bg-dark-bg/90 border border-brand-500/40 rounded-xl space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-brand-300">
              <Loader2 className="w-4 h-4 text-brand-400 animate-spin" />
              Synthesizing Knowledge Wiki with Gemini AI Studio...
            </div>

            <div className="space-y-2 text-xs font-mono">
              <div className={`flex items-center gap-2 ${activeStep >= 1 ? 'text-emerald-300 font-bold' : 'text-slate-500'}`}>
                {activeStep > 1 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <div className="w-3.5 h-3.5 rounded-full border border-brand-400 animate-ping" />}
                1. Analyzing research domain & literature context...
              </div>

              <div className={`flex items-center gap-2 ${activeStep >= 2 ? 'text-emerald-300 font-bold' : 'text-slate-500'}`}>
                {activeStep > 2 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : activeStep === 2 ? <div className="w-3.5 h-3.5 rounded-full border border-brand-400 animate-ping" /> : null}
                2. Extracting mathematical proofs & equations (LaTeX)...
              </div>

              <div className={`flex items-center gap-2 ${activeStep >= 3 ? 'text-emerald-300 font-bold' : 'text-slate-500'}`}>
                {activeStep > 3 ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : activeStep === 3 ? <div className="w-3.5 h-3.5 rounded-full border border-brand-400 animate-ping" /> : null}
                3. Constructing reference lineage & ancestry tree...
              </div>

              <div className={`flex items-center gap-2 ${activeStep >= 4 ? 'text-emerald-300 font-bold' : 'text-slate-500'}`}>
                {activeStep === 4 ? <div className="w-3.5 h-3.5 rounded-full border border-brand-400 animate-ping" /> : null}
                4. Formatting high-yield spaced repetition flashcards...
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={tab === 'topic' ? handleProcessTopic : tab === 'arxiv' ? handleProcessArxiv : handleProcessPdf}
            disabled={isProcessing || (tab === 'pdf' && !pdfFile)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-brand-600 to-cyan-600 hover:from-brand-500 hover:to-cyan-500 text-white rounded-xl text-xs font-bold shadow-glow-blue transition-all disabled:opacity-50"
          >
            {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {error ? 'Retry Generation' : 'Generate Wiki & Flashcards'}
          </button>
        </div>

      </div>
    </div>
  );
};
